import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageCircle, X, Send, Sparkles, Ticket, User, Bot } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const AIChatbot = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your DisputeKing.AI assistant. I can help you with questions about credit repair, dispute letters, using our platform, and more. How can I assist you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketData, setTicketData] = useState({
    subject: '',
    description: '',
    priority: 'medium'
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai-chat`, {
        messages: [...messages, userMessage],
        user_id: user?.id
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I\'m having trouble connecting right now. Please try creating a support ticket for immediate assistance.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketData.subject || !ticketData.description) {
      toast.error('Please fill in all ticket fields');
      return;
    }

    try {
      await axios.post(`${API}/support-tickets`, {
        ...ticketData,
        chat_history: messages.slice(1) // Exclude initial greeting
      });
      
      toast.success('Support ticket created! Our team will respond soon.');
      setShowTicketForm(false);
      setTicketData({ subject: '', description: '', priority: 'medium' });
      setMessages([messages[0]]); // Reset to initial greeting
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 w-16 h-16 bg-gradient-to-br from-violet-600 to-purple-600 rounded-full shadow-2xl hover:shadow-violet-500/50 hover:scale-110 transition-all duration-300 flex items-center justify-center z-50 group"
      >
        <MessageCircle className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
      </button>

      {/* Chat Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 p-6 text-white">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <DialogTitle className="text-white text-xl font-heading">AI Support Assistant</DialogTitle>
                    <p className="text-violet-100 text-sm">Powered by DisputeKing.AI</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </DialogHeader>
          </div>

          {showTicketForm ? (
            /* Ticket Form */
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <Ticket className="w-6 h-6 text-violet-600" />
                <h3 className="text-lg font-heading font-semibold">Create Support Ticket</h3>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Subject *</label>
                <Input
                  value={ticketData.subject}
                  onChange={(e) => setTicketData({ ...ticketData, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
                <select
                  value={ticketData.priority}
                  onChange={(e) => setTicketData({ ...ticketData, priority: e.target.value })}
                  className="w-full rounded-xl border border-slate-300 p-2 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description *</label>
                <Textarea
                  value={ticketData.description}
                  onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })}
                  placeholder="Please provide detailed information about your issue..."
                  className="rounded-xl min-h-[150px]"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowTicketForm(false)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Back to Chat
                </Button>
                <Button
                  onClick={handleCreateTicket}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  Submit Ticket
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 max-h-[400px]">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' 
                        ? 'bg-violet-100' 
                        : 'bg-gradient-to-br from-violet-500 to-purple-500'
                    }`}>
                      {message.role === 'user' ? (
                        <User className="w-4 h-4 text-violet-600" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`flex-1 rounded-2xl p-4 ${
                      message.role === 'user'
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl p-4">
                      <div className="flex gap-2">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-slate-200 p-4 space-y-3">
                <div className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your question..."
                    className="flex-1 rounded-full"
                    disabled={loading}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={loading || !input.trim()}
                    className="rounded-full bg-violet-600 hover:bg-violet-700 text-white px-6"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  onClick={() => setShowTicketForm(true)}
                  variant="outline"
                  className="w-full rounded-full text-sm"
                >
                  <Ticket className="w-4 h-4 mr-2" />
                  Need more help? Create Support Ticket
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIChatbot;
